import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';

import { CmsCore, CmsLineDataPlanCollection, CmsPlanSwitch } from 'store/cms/models';
import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';

import { RequestStatus } from 'store/request-status/models';
import { ProgressStatus, REQUEST_INTERVAL, REQUEST_TIMEOUT } from 'my-account/constants';
import { PlanEligibility } from 'store/line/models';
import { LineDataPlan } from 'store/line/models/line-data-plan';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { User } from 'store/user/models';
import { CONFIG_TOKEN, PlanCardsStatus, unlPlanNames } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { XmcPlanSwitchService } from 'my-account/activity/shared/services/xmc-plan-switch.service';

@Component({
    selector: 'plan-switch-in-progress',
    styleUrls: ['./in-progress.scss'],
    templateUrl: './in-progress.html',
    standalone: false
})
export class PlanSwitchInProgressComponent implements OnInit, OnDestroy {
    public pageData: CmsPlanSwitchData;
    public selectedPlanData: CmsDataPlanDetails;
    public isUnlimited: boolean;
    public iconOption: MediaImageOptions;
    public subhead: string;
    public planEligibility: PlanEligibility;
    public eligiblePlanDetails: LineDataPlan;
    public checkmarkIcon: MediaImageOptions;
    public user: User;
    public isCbmUserSegmentFiveTabletUnlimited: boolean = false;
    public isBusinessUser: boolean;
    public lineSummary: LineSummary;
    public targetPlanDetails: targetPlanSwitchDetails;
    public eligiblePlanSevenDetails: PlanCardsDetails;
    public inUseOnAccount: boolean;
    public pricePerUnit: string;
    public isBillPreviewEnabled: boolean = false;
    public isSeg6ResiUser: boolean = false;
    public isCbmGemini: boolean = false;

    private requestApiInterval: NodeJS.Timer;
    private switchTimeout: NodeJS.Timer;
    private xmStore: XmStore;
    private state: StateService;
    private subscriptions: Subscription[] = [];
    private planCode: string;
    private cimaCore: CimaCore;
    private config: IXMOptions;
    private xmcPlanSwitchService: XmcPlanSwitchService;

    constructor(state: StateService, xmStore: XmStore, cimaCore: CimaCore, xmcPlanSwitchService: XmcPlanSwitchService, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { state, xmStore, cimaCore, config, xmcPlanSwitchService });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.pageData = result.requestSwitch;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.checkmarkIcon = {
                mobile: Cloudinary.generateMediaFromCms(result.icons.greenCircleCheck)
            };
        }));

        this.planCode = decodeURIComponent(this.state.params.plan_code);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.selectedPlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.planCode));
            this.iconOption = {
                mobile: Cloudinary.generateMediaFromCms(this.selectedPlanData.miniIcon)
            };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.lineSummary = accountSummary.getLineByKey(this.state.params.line_id);
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.isCbmUserSegmentFiveTabletUnlimited = this.isBusinessUser && this.user.account.isSegmentFive && (this.lineSummary.isUnlBasic || this.lineSummary.isUnlPremium) && this.lineSummary.isTablet;
            this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.isBusinessUser);
        }));
        this.isSeg6ResiUser = this.user.account.isSegmentSix && !this.isBusinessUser;
        this.isCbmGemini = this.config.ENABLE_CBM_GEMINI && this.user.account.isNoLevel && this.isBusinessUser;

        if (this.user.account.isSegmentFive || this.isSeg6ResiUser || this.isCbmGemini) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (result: targetPlanSwitchDetails) => {
                this.targetPlanDetails = result;
                this.eligiblePlanSevenDetails = this.targetPlanDetails.selectionPage.planCards.find((plan: PlanCardsDetails) => plan.planCode === this.planCode);
                this.isUnlimited = unlPlanNames.includes(this.eligiblePlanSevenDetails.name.toUpperCase());
                this.pricePerUnit = this.eligiblePlanSevenDetails.price;
                this.inUseOnAccount = this.eligiblePlanSevenDetails.badge ===  PlanCardsStatus.IN_USE;
            }));
        } else {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
                filters: { lineKey: this.state.params.line_id }
            }), (planEligibility: PlanEligibility) => {
                this.planEligibility = planEligibility;
                this.eligiblePlanDetails = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === this.planCode);
                this.isUnlimited = this.eligiblePlanDetails.isUnlimited;
                this.pricePerUnit = this.isUnlimited ? this.eligiblePlanDetails.postSwitch.lineCost.toString() : this.eligiblePlanDetails.postSwitch.planGroupCost.toString();
            }));
        }

        if (this.user.account.isSegmentFive || this.user.account.isSegmentSix || this.isCbmGemini) {
            this.selectedPlanData.planName = this.eligiblePlanSevenDetails.name;
        } else if (this.isBusinessUser && !this.isCbmGemini) {
            this.selectedPlanData.planName = this.isCbmUserSegmentFiveTabletUnlimited ? this.selectedPlanData.tabletPlanName : this.selectedPlanData.cbmPlanName;
        } else {
            // eslint-disable-next-line no-self-assign
            this.selectedPlanData.planName = this.selectedPlanData.planName;
        }

        this.buildSubhead();
        if (this.isSeg6ResiUser || this.isCbmGemini) {
            this.switchPlanForPlanEight();
        } else {
            this.switchPlan();
        }
    }

    public switchPlan(): void {
        // for non seg 5 users, Order Tech Switch Plan api is called directly
        // for seg 5 users when plan 7 flags are turned off , then  Order Tech switch plan api is called directly
        if (!this.user.account.isSegmentFive && !this.user.account.isSegmentSix && !this.user.account.isNoLevel) {
            this.xmStore.persist<RequestStatus>(StoreAction.SWITCH_PLAN_VIA_SSM_PROXY, { lineId: this.state.params.line_id, planId: this.state.params.plan_code, ...(this.isBillPreviewEnabled && { trialFeature: 'TRIAL_EBP' })})
                .then((planStatus: RequestStatus) => {
                // API return 201- In case of Schedule Switch
                    if (planStatus.scheduledDate && planStatus.monthlyCost) {
                        this.state.go('account.activity.details.plan-switch.scheduled-in-progress', { line_id: this.state.params.line_id, plan_code: this.planCode, scheduled_date: planStatus.scheduledDate });
                        this.refetchApis();
                    } else {
                    // every 5 seconds, call request status api unless got back success or failure status
                        this.requestApiInterval = setInterval(() => {
                            this.queryStatus(planStatus.requestId.key).then((requestStatus: RequestStatus) => {
                                if (requestStatus.status === ProgressStatus.SUCCESS || requestStatus.status === ProgressStatus.FAIL) {
                                // if status is SUCCESS or FAIL, complete Plan Switch right away
                                    this.completePlanSwitch(requestStatus.status);
                                } else if (!this.switchTimeout && requestStatus.status === ProgressStatus.IN_PROGRESS) {
                                // set timeout only once when got status IN_PROGRESS
                                    this.switchTimeout = setTimeout(() => {
                                        this.completePlanSwitch(ProgressStatus.IN_PROGRESS);
                                    }, REQUEST_TIMEOUT);
                                }
                            }).catch(() => {
                            // set timeout if request api is failing
                                this.switchTimeout = setTimeout(() => {
                                    this.completePlanSwitch(ProgressStatus.IN_PROGRESS);
                                }, REQUEST_TIMEOUT);
                            });
                        }, REQUEST_INTERVAL);
                    }
                }).catch(() => {
                // if plan switch api fails then display failure dialog
                    this.completePlanSwitch(ProgressStatus.FAIL);
                });
        } else {
            if (this.triggerCancelSwitch()) {
                this.xmStore.persist(StoreAction.POST_CANCEL_PLAN_SCHEDULED_DOWNGRADE_VIA_SSM_PROXY, {
                    lineKey: this.state.params.line_id,
                    planId: this.planCode
                }).then(() => {
                    this.clearModel();
                    const cancelAction: string = (this.user.account.isSegmentSix && !this.isBusinessUser)
                        ? StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT : this.isCbmGemini? StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL : StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
                    this.xmStore.fetchPromise<AccountSummary>(cancelAction).then(() => {
                        this.state.go('account.activity.details.plan-switch.success', { line_id: this.state.params.line_id, plan_code: this.planCode, status: ProgressStatus.SUCCESS, isCancelScheduledPlan: true });
                    });
                }).catch(() => {
                    this.state.go('account.activity.details.plan-switch.failure', { line_id: this.state.params.line_id, plan_code: this.planCode, status: ProgressStatus.FAIL });
                });
            } else {
                this.xmStore.persist<RequestStatus>(StoreAction.SWITCH_PLAN_VIA_SSM_PROXY, { lineId: this.state.params.line_id, planId: this.state.params.plan_code, ...(this.isBillPreviewEnabled && { trialFeature: 'TRIAL_EBP' }) })
                    .then((planStatus: RequestStatus) => {
                        // API return 201- In case of Schedule Switch
                        if (planStatus.scheduledDate && planStatus.monthlyCost) {
                            this.state.go('account.activity.details.plan-switch.scheduled-in-progress', { line_id: this.state.params.line_id, plan_code: this.planCode, scheduled_date: planStatus.scheduledDate });
                            this.refetchApis();
                        } else {
                            // every 5 seconds, call request status api unless got back success or failure status
                            this.requestApiInterval = setInterval(() => {
                                this.queryStatus(planStatus.requestId.key).then((requestStatus: RequestStatus) => {
                                    if (requestStatus.status === ProgressStatus.SUCCESS || requestStatus.status === ProgressStatus.FAIL) {
                                        // if status is SUCCESS or FAIL, complete Plan Switch right away
                                        this.completePlanSwitch(requestStatus.status);
                                    } else if (!this.switchTimeout && requestStatus.status === ProgressStatus.IN_PROGRESS) {
                                        // set timeout only once when got status IN_PROGRESS
                                        this.switchTimeout = setTimeout(() => {
                                            this.completePlanSwitch(ProgressStatus.IN_PROGRESS);
                                        }, REQUEST_TIMEOUT);
                                    }
                                }).catch(() => {
                                    // set timeout if request api is failing
                                    this.switchTimeout = setTimeout(() => {
                                        this.completePlanSwitch(ProgressStatus.IN_PROGRESS);
                                    }, REQUEST_TIMEOUT);
                                });
                            }, REQUEST_INTERVAL);
                        }
                    }).catch(() => {
                        // if plan switch api fails then display failure dialog
                        this.completePlanSwitch(ProgressStatus.FAIL);
                    });
            }
        }
            
        
    }

    public switchPlanForPlanEight(): void {
        if (this.triggerCancelSwitch()) {
            this.xmStore.persist(StoreAction.POST_CANCEL_PLAN_SCHEDULED_DOWNGRADE_VIA_SSM_PROXY, {
                lineKey: this.state.params.line_id,
                planId: this.planCode
            }).then(() => {
                this.clearModel();
                let cancelAction: string = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
                if (this.isCbmGemini) {
                    cancelAction = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
                } else if (this.isSeg6ResiUser) {
                    cancelAction = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
                }
                this.xmStore.fetchPromise<AccountSummary>(cancelAction).then(() => {
                    this.state.go('account.activity.details.plan-switch.success', { line_id: this.state.params.line_id, plan_code: this.planCode, status: ProgressStatus.SUCCESS, isCancelScheduledPlan: true});
                });
            }).catch(() => {
                this.state.go('account.activity.details.plan-switch.failure', { line_id: this.state.params.line_id, plan_code: this.planCode, status: ProgressStatus.FAIL });
            });
        } else {
            const xmcPayloadForPlanSwitch: object | undefined = this.xmcPlanSwitchService.getXmcPayloadForPlanSwitch();
            const selectedLineIDKeys: string[] = this.xmcPlanSwitchService.getSelectedLineIdKeys();
            let planSwitchPayload: object;

            if (selectedLineIDKeys.length > 0) {
                planSwitchPayload = { ...xmcPayloadForPlanSwitch, ...(selectedLineIDKeys.length > 0 && { is_downgrade: true })};
            } else {
                planSwitchPayload = {
                    lineIdKey: this.state.params.line_id,
                    planId: this.state.params.plan_code
                };
            }

            this.xmStore.persist<RequestStatus>(StoreAction.SWITCH_PLAN8_VIA_SSM_PROXY, planSwitchPayload)
                .then((planStatus: RequestStatus) => {
                    // API return 201- In case of Schedule Switch
                    if (planStatus.scheduledDate && planStatus.monthlyCost) {
                        this.state.go('account.activity.details.plan-switch.scheduled-in-progress', { line_id: this.state.params.line_id, plan_code: this.planCode, scheduled_date: planStatus.scheduledDate });
                        this.refetchApis();
                    } else {
                        // every 5 seconds, call request status api unless got back success or failure status
                        this.requestApiInterval = setInterval(() => {
                            this.queryStatus(planStatus.requestId.key).then((requestStatus: RequestStatus) => {
                                if (requestStatus.status === ProgressStatus.SUCCESS || requestStatus.status === ProgressStatus.FAIL) {
                                    // if status is SUCCESS or FAIL, complete Plan Switch right away
                                    this.completePlanSwitch(requestStatus.status);
                                } else if (!this.switchTimeout && requestStatus.status === ProgressStatus.IN_PROGRESS) {
                                    // set timeout only once when got status IN_PROGRESS
                                    this.switchTimeout = setTimeout(() => {
                                        this.completePlanSwitch(ProgressStatus.IN_PROGRESS);
                                    }, REQUEST_TIMEOUT);
                                }
                            }).catch(() => {
                                // set timeout if request api is failing
                                this.switchTimeout = setTimeout(() => {
                                    this.completePlanSwitch(ProgressStatus.IN_PROGRESS);
                                }, REQUEST_TIMEOUT);
                            });
                        }, REQUEST_INTERVAL);
                    }
                }).catch(() => {
                    // if plan switch api fails then display failure dialog
                    this.completePlanSwitch(ProgressStatus.FAIL);
                });
        }
    }

    public triggerCancelSwitch(): boolean {
        if (this.targetPlanDetails && this.targetPlanDetails.scheduledTargetPlan && (this.user.account.isSegmentFive || this.isSeg6ResiUser || this.isCbmGemini)) {
            const matchedPlan: PlanCardsDetails = this.targetPlanDetails.selectionPage.planCards.find((plan: PlanCardsDetails) => plan.planCode === this.planCode);
            if (matchedPlan && matchedPlan.badge === PlanCardsStatus.IN_USE) {
                return true;
            }
        }
        
        return false;
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public completePlanSwitch(planSwitchStatus: string): void {
        clearInterval(this.requestApiInterval);
        clearTimeout(this.switchTimeout);

        switch (planSwitchStatus) {
            case ProgressStatus.IN_PROGRESS:
                this.state.go('account.activity.details.plan-switch.pending', { line_id: this.state.params.line_id, plan_code: this.state.params.plan_code, status: ProgressStatus.IN_PROGRESS });
                break;
            case ProgressStatus.SUCCESS:
                this.state.go('account.activity.details.plan-switch.success', { line_id: this.state.params.line_id, plan_code: this.state.params.plan_code, status: ProgressStatus.SUCCESS });
                break;
            case ProgressStatus.FAIL:
                this.state.go('account.activity.details.plan-switch.failure', { line_id: this.state.params.line_id, plan_code: this.state.params.plan_code, status: ProgressStatus.FAIL });
                break;
        }
    }

    public buildHeader(): string {
        if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
            return this.eligiblePlanSevenDetails.name;
        } else {
            if (this.isUnlimited) {
                return Util.createPascalCase(this.selectedPlanData.planType);
            } else if (this.isBusinessUser) {
                return this.selectedPlanData.cbmPlanName;
            } else {
                return this.selectedPlanData.planName;
            }
        }
    }

    private clearModel(): void {
        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_LINE_LEVEL_PLAN_ELIGIBILITY },
            { action: StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY },
            { action: StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT }
        ];

        this.xmStore.clearApis(clearApisList);
    }

    private buildSubhead(): void {
        const planName: string = (this.user.account.isSegmentFive || this.user.account.isSegmentSix || this.isCbmGemini) ? this.eligiblePlanSevenDetails.name : this.isBusinessUser ? this.selectedPlanData.cbmPlanName : this.selectedPlanData.planName;
        this.subhead = this.pageData.subhead.replace(/\[PLAN_NAME\]/g, planName);

        if (!this.isUnlimited && (!this.user.account.isSegmentFive)) {
            this.subhead = `${this.subhead}, ${this.eligiblePlanDetails.sharedDataGB} GB`;
        }
    }

    private queryStatus(requestId: string): Promise<RequestStatus> {
        return this.xmStore.queryPromise<RequestStatus>(StoreAction.GET_REQUEST_STATUS, { requestId });
    }

    private refetchApis(): void {
        //Get scheduled details from refetch Account summary api.
        let action: string;

        if (this.config.ENABLE_CBM_GEMINI && this.cimaCore.isBusinessUser && this.user.account.isNoLevel) {
            action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
        } else if (this.user.account.isSegmentSix && !this.cimaCore.isBusinessUser) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
        } else if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
        } else {
            action = StoreAction.GET_ACCOUNT_SUMMARY;
        }

        this.xmStore.fetchPromise<AccountSummary>(action);
    }
}
